import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { ExternalLinkIcon, CheckIcon, XIcon, ArrowLeftIcon } from 'lucide-react';
import { Card } from '../components/Card';
import { TrustScore } from '../components/TrustScore';
import { ReviewCard } from '../components/ReviewCard';
import { Button } from '../components/Button';
import { SocialShare } from '../components/SocialShare';
import { exchanges } from '../data/mockData';
export function ExchangeDetailPage() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const exchange = exchanges.find(e => e.id === id);
  if (!exchange) {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-id="element-448">
        <div className="text-center" data-id="element-449">
          <h1 className="text-2xl font-bold mb-4" data-id="element-450">Exchange not found</h1>
          <Link to="/exchanges" data-id="element-451">
            <Button data-id="element-452">Back to Exchanges</Button>
          </Link>
        </div>
      </div>;
  }
  return <div className="min-h-screen bg-slate-50" data-id="element-453">
      {/* Header */}
      <div className="bg-gradient-to-br from-slate-900 to-blue-900 text-white py-12" data-id="element-454">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-455">
          <div className="flex items-center justify-between mb-6" data-id="element-456">
            <Link to="/exchanges" className="inline-flex items-center gap-2 text-slate-300 hover:text-white transition-colors" data-id="element-457">
              <ArrowLeftIcon size={20} data-id="element-458" />
              <span data-id="element-459">Back to Exchanges</span>
            </Link>
            <SocialShare title={`${exchange.name} - Crypto Exchange Review & Analysis`} description={`Comprehensive review of ${exchange.name} exchange - trading fees, supported coins, and user ratings on CryptoTopReviews`} hashtags={['crypto', 'exchange', exchange.name.toLowerCase().replace(/\s+/g, '')]} data-id="element-460" />
          </div>

          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6" data-id="element-461">
            <div className="flex items-center gap-4" data-id="element-462">
              <img src={exchange.logo} alt={exchange.name} className="w-16 h-16 rounded-xl bg-white/10 p-2" data-id="element-463" />
              <div data-id="element-464">
                <h1 className="text-4xl font-bold mb-2" data-id="element-465">{exchange.name}</h1>
                <TrustScore score={exchange.trustScore} size="md" data-id="element-466" />
              </div>
            </div>

            <a href={exchange.website} target="_blank" rel="noopener noreferrer" data-id="element-467">
              <Button size="lg" className="flex items-center gap-2" data-id="element-468">
                <span data-id="element-469">Visit Exchange</span>
                <ExternalLinkIcon size={18} data-id="element-470" />
              </Button>
            </a>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-471">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-472">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8" data-id="element-473">
            {/* Key Metrics */}
            <Card className="p-6" data-id="element-474">
              <h2 className="text-2xl font-bold mb-6" data-id="element-475">Key Metrics</h2>
              <div className="grid grid-cols-2 gap-6" data-id="element-476">
                <div data-id="element-477">
                  <div className="text-sm text-slate-500 mb-1" data-id="element-478">
                    24h Trading Volume
                  </div>
                  <div className="text-xl font-semibold" data-id="element-479">
                    ${(exchange.tradingVolume / 1000000000).toFixed(2)}B
                  </div>
                </div>
                <div data-id="element-480">
                  <div className="text-sm text-slate-500 mb-1" data-id="element-481">
                    Supported Coins
                  </div>
                  <div className="text-xl font-semibold" data-id="element-482">
                    {exchange.supportedCoins}
                  </div>
                </div>
                <div data-id="element-483">
                  <div className="text-sm text-slate-500 mb-1" data-id="element-484">Trading Fee</div>
                  <div className="text-xl font-semibold" data-id="element-485">
                    {exchange.tradingFee}
                  </div>
                </div>
                <div data-id="element-486">
                  <div className="text-sm text-slate-500 mb-1" data-id="element-487">
                    Withdrawal Fee
                  </div>
                  <div className="text-xl font-semibold" data-id="element-488">
                    {exchange.withdrawalFee}
                  </div>
                </div>
              </div>
            </Card>

            {/* About */}
            <Card className="p-6" data-id="element-489">
              <h2 className="text-2xl font-bold mb-4" data-id="element-490">About {exchange.name}</h2>
              <p className="text-slate-700 leading-relaxed mb-6" data-id="element-491">
                {exchange.description}
              </p>
            </Card>

            {/* Features */}
            <Card className="p-6" data-id="element-492">
              <h2 className="text-2xl font-bold mb-4" data-id="element-493">Features</h2>
              <div className="flex flex-wrap gap-2" data-id="element-494">
                {exchange.features.map((feature, index) => <span key={index} className="bg-blue-100 text-blue-700 px-3 py-1.5 rounded-lg text-sm font-medium" data-id="element-495">
                    {feature}
                  </span>)}
              </div>
            </Card>

            {/* Pros & Cons */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-496">
              <Card className="p-6" data-id="element-497">
                <h3 className="text-xl font-bold mb-4 flex items-center gap-2" data-id="element-498">
                  <CheckIcon className="text-green-600" size={24} data-id="element-499" />
                  <span data-id="element-500">Pros</span>
                </h3>
                <ul className="space-y-2" data-id="element-501">
                  {exchange.pros.map((pro, index) => <li key={index} className="flex items-start gap-2" data-id="element-502">
                      <CheckIcon className="text-green-600 flex-shrink-0 mt-0.5" size={16} data-id="element-503" />
                      <span className="text-slate-700" data-id="element-504">{pro}</span>
                    </li>)}
                </ul>
              </Card>

              <Card className="p-6" data-id="element-505">
                <h3 className="text-xl font-bold mb-4 flex items-center gap-2" data-id="element-506">
                  <XIcon className="text-red-600" size={24} data-id="element-507" />
                  <span data-id="element-508">Cons</span>
                </h3>
                <ul className="space-y-2" data-id="element-509">
                  {exchange.cons.map((con, index) => <li key={index} className="flex items-start gap-2" data-id="element-510">
                      <XIcon className="text-red-600 flex-shrink-0 mt-0.5" size={16} data-id="element-511" />
                      <span className="text-slate-700" data-id="element-512">{con}</span>
                    </li>)}
                </ul>
              </Card>
            </div>

            {/* Reviews */}
            <div data-id="element-513">
              <h2 className="text-2xl font-bold mb-6" data-id="element-514">User Reviews</h2>
              <div className="space-y-4" data-id="element-515">
                {exchange.reviews.map(review => <ReviewCard key={review.id} review={review} data-id="element-516" />)}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6" data-id="element-517">
            <Card className="p-6" data-id="element-518">
              <h3 className="font-bold text-lg mb-4" data-id="element-519">Quick Stats</h3>
              <div className="space-y-4" data-id="element-520">
                <div className="flex justify-between items-center pb-3 border-b border-slate-100" data-id="element-521">
                  <span className="text-slate-600" data-id="element-522">Rank</span>
                  <span className="font-semibold" data-id="element-523">#{exchange.rank}</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-slate-100" data-id="element-524">
                  <span className="text-slate-600" data-id="element-525">Trading Fee</span>
                  <span className="font-semibold" data-id="element-526">{exchange.tradingFee}</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-slate-100" data-id="element-527">
                  <span className="text-slate-600" data-id="element-528">Coins Listed</span>
                  <span className="font-semibold" data-id="element-529">
                    {exchange.supportedCoins}
                  </span>
                </div>
                <div className="flex justify-between items-center" data-id="element-530">
                  <span className="text-slate-600" data-id="element-531">Total Reviews</span>
                  <span className="font-semibold" data-id="element-532">
                    {exchange.reviews.length}
                  </span>
                </div>
              </div>
            </Card>

            <Card className="p-6 bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200" data-id="element-533">
              <h3 className="font-bold text-lg mb-2" data-id="element-534">Trust Score Breakdown</h3>
              <div className="text-3xl font-bold text-blue-600 mb-4" data-id="element-535">
                {exchange.trustScore.toFixed(1)}/10
              </div>
              <p className="text-sm text-slate-700 leading-relaxed" data-id="element-536">
                Our trust score evaluates security measures, regulatory
                compliance, user experience, and customer support quality.
              </p>
            </Card>

            <Card className="p-6" data-id="element-537">
              <h3 className="font-bold text-lg mb-4" data-id="element-538">Official Links</h3>
              <a href={exchange.website} target="_blank" rel="noopener noreferrer" className="block" data-id="element-539">
                <Button variant="outline" className="w-full flex items-center justify-center gap-2" data-id="element-540">
                  <span data-id="element-541">Visit Website</span>
                  <ExternalLinkIcon size={16} data-id="element-542" />
                </Button>
              </a>
            </Card>
          </div>
        </div>
      </div>
    </div>;
}